import { useState, useMemo, useEffect, createContext, useContext } from "react";
import { Badge } from "@/components/ui/badge";
import { fmtHours, fmtDate } from "@/lib/api";
import { usePageContent } from "@/hooks/usePageContent";
import { useAuth } from "@/context/AuthContext";
import { useLeaveTypes } from "@/context/LeaveTypesContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { format, parse, isValid } from "date-fns";
import { ArrowUp, ArrowDown, ChevronsUpDown, Eye, EyeOff, Calendar as CalendarIcon } from "lucide-react";

// ---- Sensitive figure shielding (blur-to-reveal, like the admin salary cost) ----
const RevealContext = createContext({ pinned: false, setPinned: () => {} });

// Wrap a section/card. Figures inside <Sensitive> are blurred until hovered (group hover)
// or pinned open via <ShieldToggle />. Renders a div carrying the group/shield marker.
export function Shield({ children, className = "", testid }) {
  const [pinned, setPinned] = useState(false);
  return (
    <RevealContext.Provider value={{ pinned, setPinned }}>
      <div className={`group/shield ${className}`} data-testid={testid}>{children}</div>
    </RevealContext.Provider>
  );
}

// Blurs its children until revealed: on hover of the figure itself, on hover anywhere
// in the surrounding Shield, or when the Shield's toggle is pinned open.
export function Sensitive({ children, testid, className = "" }) {
  const { pinned } = useContext(RevealContext);
  return (
    <span data-testid={testid}
      className={`transition-[filter] duration-200 ${pinned ? "" : "blur-[6px] hover:blur-0 group-hover/shield:blur-0 select-none"} ${className}`}>
      {children}
    </span>
  );
}

// Eye button that pins/unpins reveal for all Sensitive figures in the surrounding Shield.
export function ShieldToggle({ className = "", testid = "reveal-toggle" }) {
  const { pinned, setPinned } = useContext(RevealContext);
  return (
    <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPinned((s) => !s); }}
      className={`text-slate-400 hover:text-[#2EA3FD] transition-colors shrink-0 ${className}`}
      data-testid={testid} aria-label={pinned ? "Hide figures" : "Reveal figures"} title={pinned ? "Hide figures" : "Reveal figures"}>
      {pinned ? <EyeOff size={15} /> : <Eye size={15} />}
    </button>
  );
}

// Typeable date input + calendar popover with day-level selection and month/year dropdowns.
// value/onChange use ISO "yyyy-MM-dd" strings; display is UK dd/MM/yyyy.
export function DateField({ value, onChange, testid, placeholder = "dd/mm/yyyy",
  fromYear = 1930, toYear = new Date().getFullYear() + 1, className = "" }) {
  const parseIso = (s) => (s && isValid(parse(s, "yyyy-MM-dd", new Date())) ? parse(s, "yyyy-MM-dd", new Date()) : undefined);
  const dateObj = parseIso(value || "");
  const [text, setText] = useState(dateObj ? format(dateObj, "dd/MM/yyyy") : "");
  const [open, setOpen] = useState(false);
  useEffect(() => { setText(dateObj ? format(dateObj, "dd/MM/yyyy") : ""); }, [value]); // eslint-disable-line
  const commit = (t) => {
    const s = (t || "").trim();
    if (!s) { onChange(""); return; }
    let d = parse(s, "dd/MM/yyyy", new Date());
    if (!isValid(d)) d = parse(s, "d/M/yyyy", new Date());
    if (!isValid(d)) d = parse(s, "yyyy-MM-dd", new Date());
    if (isValid(d)) { onChange(format(d, "yyyy-MM-dd")); setText(format(d, "dd/MM/yyyy")); }
  };
  return (
    <div className={`relative ${className}`}>
      <Input value={text} placeholder={placeholder} data-testid={testid} className="pr-9"
        onChange={(e) => setText(e.target.value)}
        onBlur={() => commit(text)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(text); } }} />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" aria-label="Open calendar" data-testid={testid ? `${testid}-calendar-btn` : undefined}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#2EA3FD]">
            <CalendarIcon size={16} />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" captionLayout="dropdown-buttons" fromYear={fromYear} toYear={toYear}
            selected={dateObj} defaultMonth={dateObj}
            classNames={{ caption_label: "hidden", vhidden: "hidden", caption_dropdowns: "flex gap-1",
              dropdown: "text-sm rounded-md border border-slate-200 bg-white px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-[#2EA3FD]" }}
            onSelect={(d) => { if (d) { onChange(format(d, "yyyy-MM-dd")); setText(format(d, "dd/MM/yyyy")); } setOpen(false); }} />
        </PopoverContent>
      </Popover>
    </div>
  );
}

// Admin-only chip shown on live pages where a Page-content section is switched off,
// so admins can spot hidden content blocks at a glance (staff see nothing).
export function SectionHidden({ enabled, label }) {
  const { user } = useAuth();
  if (enabled !== false) return null;
  if (user?.role !== "admin") return null;
  return (
    <div data-testid="section-hidden-chip" className="flex items-center gap-2 rounded-lg border border-dashed border-amber-300 bg-amber-50/70 text-amber-700 px-4 py-2.5 text-xs">
      <EyeOff size={14} className="shrink-0" />
      <span><strong>{label}</strong> is hidden from staff — only you (admin) see this note. Turn it back on in Settings › Page content.</span>
    </div>
  );
}

// Reusable click-to-sort table utilities.
export function useTableSort(items, accessors, initial = { key: null, dir: "asc" }) {
  const [sort, setSort] = useState(initial);
  const sorted = useMemo(() => {
    if (!sort.key || !accessors[sort.key]) return items;
    const acc = accessors[sort.key];
    return [...items].sort((a, b) => {
      let av = acc(a), bv = acc(b);
      if (av === null || av === undefined) av = "";
      if (bv === null || bv === undefined) bv = "";
      let cmp;
      if (typeof av === "number" && typeof bv === "number") cmp = av - bv;
      else cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" });
      return sort.dir === "asc" ? cmp : -cmp;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, sort]);
  const toggle = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  return { sorted, sort, toggle };
}

// Reusable circular avatar: shows profile photo if present, else coloured initials.
const AVATAR_COLORS = ["#2EA3FD", "#0B1A2F", "#10b981", "#8b5cf6", "#f59e0b", "#ef4444", "#14b8a6", "#ec4899"];
export function Avatar({ name = "", photo, size = 28, className = "", color }) {
  const initials = String(name).split(" ").filter(Boolean).map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "?";
  const code = String(name).split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  const bg = color || AVATAR_COLORS[code % AVATAR_COLORS.length];
  const style = { width: size, height: size, fontSize: Math.round(size * 0.38) };
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center rounded-full overflow-hidden font-bold text-white ring-1 ring-white shadow-sm ${className}`}
      style={photo ? style : { ...style, backgroundColor: bg }}
      title={name}
      data-testid="avatar"
    >
      {photo ? <img src={photo} alt={name} className="h-full w-full object-cover" /> : initials}
    </span>
  );
}


export function SortableTH({ label, sortId, sort, onSort, className = "" }) {
  const active = sort.key === sortId;
  return (
    <TableHead className={`text-xs uppercase tracking-wider text-slate-600 cursor-pointer select-none hover:text-[#0B1A2F] ${className}`} onClick={() => onSort(sortId)} data-testid={`sort-${sortId}`}>
      <span className="inline-flex items-center gap-1">{label}
        {active ? (sort.dir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ChevronsUpDown size={12} className="opacity-30" />}
      </span>
    </TableHead>
  );
}

const RAG = {
  green: { dot: "bg-emerald-500", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Good" },
  amber: { dot: "bg-amber-500", cls: "bg-amber-50 text-amber-700 border-amber-200", label: "Attention" },
  red: { dot: "bg-rose-500", cls: "bg-rose-50 text-rose-700 border-rose-200", label: "Action needed" },
};

const RAG_DESC = {
  green: "Healthy — the leave balance is on track for the year with no issues.",
  amber: "Attention — the balance is running low, or setup needs a look (e.g. no working pattern defined).",
  red: "Action needed — entitlement has been exceeded or leave is over-booked.",
};

export function RagBadge({ status, text }) {
  const r = RAG[status] || RAG.green;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span data-testid="employee-rag-status-badge"
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold cursor-help ${r.cls}`}>
            <span className={`h-2 w-2 rounded-full ${r.dot}`} />
            {text || r.label}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs leading-snug" data-testid="rag-tooltip">
          {RAG_DESC[status] || RAG_DESC.green}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

const LEAVE = {
  annual: "bg-sky-50 text-sky-700 border-sky-200",
  bank_holiday: "bg-indigo-50 text-indigo-700 border-indigo-200",
  carry_forward: "bg-violet-50 text-violet-700 border-violet-200",
  unpaid: "bg-gray-100 text-gray-700 border-gray-300",
  other: "bg-slate-100 text-slate-700 border-slate-300",
};
const LEAVE_LABEL = {
  annual: "Annual", bank_holiday: "Bank holiday", carry_forward: "Carried-forward",
  unpaid: "Unpaid", other: "Other authorised",
};
export function LeaveTypeBadge({ type }) {
  const { map } = useLeaveTypes();
  const t = map[type];
  const name = t?.name || (LEAVE_LABEL[type] || String(type || "").replace(/_/g, " "));
  const color = t?.color || "#64748B";
  return (
    <span data-testid={`leave-type-badge-${type}`}
      style={{ color, borderColor: `${color}55`, backgroundColor: `${color}18` }}
      className="inline-flex rounded-md border px-2 py-0.5 text-xs font-medium capitalize">
      {name}
    </span>
  );
}

const STATUS = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  declined: "bg-rose-50 text-rose-700 border-rose-200",
  cancelled: "bg-slate-100 text-slate-500 border-slate-200",
};
export function StatusBadge({ status }) {
  return <span data-testid={`status-badge-${status}`} className={`inline-flex rounded-md border px-2 py-0.5 text-xs font-semibold capitalize ${STATUS[status] || STATUS.cancelled}`}>{status}</span>;
}

export function StatCard({ label, value, sub, icon: Icon, tone = "navy", accent, testid, onClick, hint }) {
  const tones = {
    navy: "text-[#0B1A2F]", blue: "text-[#2EA3FD]", green: "text-emerald-600",
    amber: "text-amber-600", red: "text-rose-600",
  };
  const accents = {
    blue: "border-t-4 border-t-[#2EA3FD]", teal: "border-t-4 border-t-teal-500",
    green: "border-t-4 border-t-emerald-500", amber: "border-t-4 border-t-amber-500",
    navy: "border-t-4 border-t-[#0B1A2F]", red: "border-t-4 border-t-rose-500",
  };
  return (
    <div data-testid={testid} onClick={onClick}
      className={`bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-all animate-in-up ${accent ? accents[accent] : ""} ${onClick ? "cursor-pointer hover:-translate-y-0.5 hover:border-[#2EA3FD]" : ""}`}>
      <div className="flex items-start justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
        {Icon && <span className="rounded-lg bg-[#EFF8FF] p-1.5 text-[#2EA3FD]"><Icon size={16} /></span>}
      </div>
      <p className={`mt-2 text-3xl font-extrabold font-display tabular ${tones[tone]}`}>{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
      {hint && <p className="text-[11px] text-[#2EA3FD] mt-1 font-medium">{hint}</p>}
    </div>
  );
}

// Left-accent alert card used for "needs attention" style callouts.
export function AlertCard({ title, children, tone = "amber", testid }) {
  const tones = {
    amber: "border-l-amber-500 bg-amber-50/70 border-amber-200/60",
    blue: "border-l-[#2EA3FD] bg-sky-50/70 border-sky-200/60",
    red: "border-l-rose-500 bg-rose-50/70 border-rose-200/60",
  };
  return (
    <div data-testid={testid} className={`border-l-4 border-y border-r rounded-xl p-4 shadow-sm ${tones[tone]}`}>
      <p className="font-semibold text-[#0B1A2F]">{title}</p>
      <div className="text-sm text-slate-600 mt-1">{children}</div>
    </div>
  );
}

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-2">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#0B1A2F] font-display">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
      </div>
      {children && <div className="flex items-center gap-2 flex-wrap">{children}</div>}
    </div>
  );
}

// Gradient hero banner for section landing pages (Option 2 design system).
// Pass `pageKey` to make the eyebrow/title/subtitle admin-editable via Page content.
export function PageHero({ title, subtitle, icon: Icon, stats, children, tone = "blue", eyebrow = "CASS HR", pageKey }) {
  const pc = usePageContent();
  const h = pageKey ? pc?.[pageKey]?.hero : null;
  const eyebrowV = h?.eyebrow || eyebrow;
  const titleV = h?.title || title;
  const subtitleV = (h && h.subtitle) ? h.subtitle : subtitle;
  const grad = {
    blue: "from-[#0B1A2F] via-[#0F2C50] to-[#2EA3FD]",
    teal: "from-[#0B1A2F] via-[#0F2C50] to-[#36776B]",
    violet: "from-[#0B1A2F] via-[#231C51] to-[#6D5AE6]",
  }[tone] || "from-[#0B1A2F] via-[#0F2C50] to-[#2EA3FD]";
  return (
    <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${grad} text-white p-6 sm:p-7`} data-testid="page-hero">
      <div className="absolute -right-14 -top-14 h-52 w-52 rounded-full bg-white/10" />
      <div className="absolute -right-2 bottom-0 h-28 w-28 rounded-full bg-white/5" />
      <div className="relative flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-widest text-[#8FD0FF] flex items-center gap-2">{Icon && <Icon size={14} />}{eyebrowV}</p>
          <h1 className="text-2xl sm:text-3xl font-extrabold font-display mt-1">{titleV}</h1>
          {subtitleV && <p className="text-slate-200 mt-2 max-w-xl text-sm">{subtitleV}</p>}
          {stats?.length > 0 && (
            <div className="flex flex-wrap gap-3 mt-4">
              {stats.map((s, i) => (
                <div key={i} className="bg-white/10 rounded-lg px-4 py-2" data-testid={`hero-stat-${i}`}>
                  <p className="text-[11px] text-[#B9DCFF]">{s.label}</p><p className="text-xl font-bold">{s.value}</p>
                </div>
              ))}
            </div>
          )}
        </div>
        {children && <div className="flex items-center gap-2 flex-wrap shrink-0">{children}</div>}
      </div>
    </div>
  );
}

const DUR = { hours: "Specific hours", half: "Half day", full: "Full day" };

export function BookingDetailDialog({ booking, open, onOpenChange }) {
  if (!booking) return null;
  const b = booking;
  const Meta = ({ label, value }) => (value === 0 || value ? (
    <div><p className="text-xs text-slate-500">{label}</p><p className="text-sm font-medium text-[#0B1A2F]">{value}</p></div>
  ) : null);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="booking-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 flex-wrap">
            <span className="font-mono text-sm">{b.reference}</span>
            <LeaveTypeBadge type={b.leave_type} />
            <StatusBadge status={b.status} />
          </DialogTitle>
          <DialogDescription className="sr-only">Full details for this leave booking.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-2">
          <Meta label="Dates" value={`${fmtDate(b.start_date)}${b.start_date !== b.end_date ? ` – ${fmtDate(b.end_date)}` : ""}`} />
          <Meta label="Total hours" value={fmtHours(b.total_hours)} />
          <Meta label="Total days" value={b.total_days} />
          <Meta label="Duration" value={DUR[b.duration_type] || "Full day"} />
          <Meta label="Employee" value={b.employee_name} />
          <Meta label="Requested by" value={b.requested_by} />
          <Meta label="Requested on" value={b.request_date ? fmtDate(b.request_date) : null} />
          <Meta label="Approver" value={b.approver_name} />
          <Meta label="Decision date" value={b.decision_date ? fmtDate(b.decision_date) : null} />
        </div>
        {b.employee_notes && <div className="rounded-lg bg-slate-50 border p-3 mb-2"><p className="text-xs text-slate-500 mb-0.5">Employee notes</p><p className="text-sm">{b.employee_notes}</p></div>}
        {b.manager_notes && <div className="rounded-lg bg-slate-50 border p-3 mb-2"><p className="text-xs text-slate-500 mb-0.5">Manager notes</p><p className="text-sm">{b.manager_notes}</p></div>}
        {b.cancellation_reason && <div className="rounded-lg bg-rose-50 border border-rose-200 p-3 mb-2"><p className="text-xs text-rose-600 mb-0.5">Cancellation reason</p><p className="text-sm">{b.cancellation_reason}</p></div>}
        {(b.breakdown || []).length > 0 && (
          <div>
            <p className="text-sm font-semibold text-[#0B1A2F] mb-2">Day-by-day breakdown</p>
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <Table>
                <TableHeader><TableRow className="bg-slate-50">{["Date", "Day", "Week", "Scheduled", "Deducted", "Note"].map((h) => <TableHead key={h} className="text-xs uppercase text-slate-600">{h}</TableHead>)}</TableRow></TableHeader>
                <TableBody>
                  {b.breakdown.map((r) => (
                    <TableRow key={r.date} className={r.is_non_working ? "text-slate-400" : ""} data-testid={`breakdown-row-${r.date}`}>
                      <TableCell className="text-sm">{fmtDate(r.date)}</TableCell>
                      <TableCell className="text-sm">{r.weekday?.slice(0, 3)}</TableCell>
                      <TableCell className="text-center text-sm">{r.cycle_week ? `W${r.cycle_week}` : "—"}</TableCell>
                      <TableCell className="tabular text-sm">{r.scheduled_hours}h</TableCell>
                      <TableCell className="tabular text-sm font-semibold">{r.hours_deducted}h</TableCell>
                      <TableCell className="text-xs text-slate-500">{r.note}{r.is_bank_holiday ? " · Bank holiday" : ""}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export { fmtHours };
